import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import * as marketingFormsService from '../services/marketingFormsService'
import * as surveysService from '../services/surveysService'

export const agentSurveyResponsesKeys = {
  all: ['agent-survey-responses'],
  list: (agentId) => [...agentSurveyResponsesKeys.all, agentId]
}

export const leadSurveyResponsesKeys = {
  all: ['lead-survey-responses'],
  list: (leadId) => [...leadSurveyResponsesKeys.all, leadId]
}

export const surveyResponseDetailKeys = {
  detail: (responseId) => ['survey-response-detail', responseId]
}

export const surveyLeadOwnersKeys = {
  all: ['survey-lead-owners']
}

/**
 * @param {string | undefined} agentId
 * @param {{ role?: string, userId?: string }} auth
 */
export const useAgentSurveyResponses = (agentId, auth = {}, config = {}) => {
  const { role, userId } = auth
  return useQuery({
    queryKey: agentSurveyResponsesKeys.list(agentId),
    queryFn: async () => {
      const { data, error } = await surveysService.getFormResponses(agentId, {
        role,
        userId
      })
      if (error) throw error
      return data ?? []
    },
    enabled: Boolean(agentId && userId) && (config.enabled ?? true),
    staleTime: config.staleTime ?? 1000 * 60 * 2
  })
}

/**
 * @param {string | null | undefined} leadId
 * @param {{ role?: string, userId?: string }} auth
 */
export const useLeadSurveyResponses = (leadId, auth = {}, config = {}) => {
  const { role, userId } = auth
  return useQuery({
    queryKey: leadSurveyResponsesKeys.list(leadId),
    queryFn: async () => {
      const { data, error } = await marketingFormsService.listResponsesForLead(leadId, {
        role,
        userId
      })
      if (error) throw error
      return data ?? []
    },
    enabled: Boolean(leadId && userId) && (config.enabled ?? true),
    staleTime: config.staleTime ?? 1000 * 60 * 2
  })
}

/**
 * @param {string | null | undefined} responseId
 * @param {{ role?: string, userId?: string }} auth
 */
export const useSurveyResponseDetail = (responseId, auth = {}, config = {}) => {
  const { role, userId } = auth
  return useQuery({
    queryKey: surveyResponseDetailKeys.detail(responseId),
    queryFn: async () => {
      const { data, error } = await marketingFormsService.getResponseById(responseId, {
        role,
        userId
      })
      if (error) throw error
      return data
    },
    enabled: Boolean(responseId && userId) && (config.enabled ?? true),
    staleTime: config.staleTime ?? 1000 * 60 * 2
  })
}

/**
 * @param {{ role?: string, userId?: string }} auth
 */
export const useSurveyLeadOwners = (auth = {}, config = {}) => {
  const { role, userId } = auth
  return useQuery({
    queryKey: surveyLeadOwnersKeys.all,
    queryFn: async () => {
      const { data, error } = await surveysService.listLeadOwners({ role, userId })
      if (error) throw error
      return data ?? []
    },
    enabled: Boolean(userId) && (config.enabled ?? true),
    staleTime: config.staleTime ?? 1000 * 60 * 5
  })
}

/**
 * @param {string | undefined} agentId
 * @param {{ role?: string, userId?: string }} auth
 */
export const useCreateLeadFromResponse = (agentId, auth = {}) => {
  const queryClient = useQueryClient()
  const { role, userId } = auth

  return useMutation({
    mutationFn: async ({ responseId, leadData }) => {
      const { data, error } = await surveysService.createLeadFromResponse(
        responseId,
        leadData,
        { role, userId }
      )
      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: agentSurveyResponsesKeys.list(agentId) })
    }
  })
}
